import { PASSING_DISPLAY } from '../../severity';
import styles from './SegmentedBar.module.css';

export interface BarSegment {
  key: string;
  value: number;
  /** Any CSS colour, normally a --zf-sc-* var. */
  color: string;
  label: string;
}

interface SegmentedBarProps {
  segments: BarSegment[];
  ariaLabel?: string;
  className?: string;
}

/**
 * Widths are rounded percentages of the segment sum; the last visible segment
 * takes the rounding remainder so widths always sum to 100%.
 */
export const SegmentedBar = ({
  segments,
  ariaLabel,
  className,
}: SegmentedBarProps) => {
  const visible = segments.filter(s => s.value > 0);
  const sum = visible.reduce((acc, s) => acc + s.value, 0);
  let used = 0;
  const widths = visible.map((s, i) => {
    if (i === visible.length - 1) return 100 - used;
    const pct = Math.round((s.value / sum) * 100);
    used += pct;
    return pct;
  });
  const label =
    ariaLabel ?? segments.map(s => `${s.value} ${s.label}`).join(', ');
  return (
    <span
      role="img"
      aria-label={label}
      className={
        className ? `${styles.segmentedBar} ${className}` : styles.segmentedBar
      }
    >
      {visible.map((s, i) => (
        <span
          key={s.key}
          className={styles.segment}
          style={{ width: `${widths[i]}%`, backgroundColor: s.color }}
        />
      ))}
    </span>
  );
};

interface PassFailBarProps {
  passing: number;
  failing: number;
  total: number;
}

/** Pass / fail / no-run bar; checks without a snapshot fill the remainder. */
export const PassFailBar = ({ passing, failing, total }: PassFailBarProps) => (
  <SegmentedBar
    segments={[
      {
        key: 'passing',
        value: passing,
        color: PASSING_DISPLAY.colorVar,
        label: PASSING_DISPLAY.label,
      },
      {
        key: 'failing',
        value: failing,
        color: 'var(--zf-sc-fail)',
        label: 'Failing',
      },
      {
        key: 'no-run',
        value: Math.max(total - passing - failing, 0),
        color: 'var(--zf-sc-neutral)',
        label: 'No run',
      },
    ]}
  />
);
